/**
 * Safely extracts a human-readable error message from unknown error types.
 *
 * Handles:
 * - Axios errors with `response.data.error` or `response.data.message`
 * - Axios errors with non-JSON response bodies (HTML from gateway errors, etc.)
 * - Standard Error objects
 * - Plain string errors (e.g. re-thrown from api/post.ts)
 * - Fallback for anything else (undefined, null, etc.)
 */
export function getErrorMessage(error: unknown): string {
  // Already a usable string (e.g. re-thrown from api/post.ts catch blocks)
  if (typeof error === "string" && error) {
    return error;
  }

  if (error && typeof error === "object") {
    // Axios-style error with response.data
    if ("response" in error) {
      const data = (error as { response?: { data?: unknown } }).response?.data;

      if (typeof data === "object" && data !== null) {
        const obj = data as Record<string, unknown>;
        if (typeof obj.error === "string" && obj.error) return obj.error;
        if (typeof obj.message === "string" && obj.message) return obj.message;
      }

      // Gateway / proxy errors that return plain HTML or text
      if (typeof data === "string" && data) {
        return data;
      }
    }

    // Standard Error instance
    if (error instanceof Error && error.message) {
      return error.message;
    }
  }

  return "An unexpected error occurred. Please try again.";
}

/**
 * Extracts the API error for re-throwing from api/ catch blocks.
 *
 * Always returns a proper Error object so that:
 * - Bugsnag captures the actual message (strings are lost as "non-error")
 * - getErrorMessage() can extract it via instanceof Error
 * - Unhandled rejections are debuggable
 */
export function extractApiError(error: unknown): Error {
  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosErr = error as {
      response?: { data?: { error?: string; message?: string } };
    };
    const data = axiosErr.response?.data;

    if (typeof data === "object" && data !== null) {
      if (typeof data.error === "string" && data.error)
        return new Error(data.error);
      if (typeof data.message === "string" && data.message)
        return new Error(data.message);
      return new Error(JSON.stringify(data));
    }

    if (typeof data === "string" && data) {
      return new Error(data);
    }
  }

  if (error instanceof Error) {
    return error;
  }

  return new Error(
    typeof error === "string" && error ? error : "An unexpected error occurred"
  );
}
